import { Component, HostListener, OnInit, OnChanges, OnDestroy, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridModule } from 'ag-grid-angular';
import {
  AllCommunityModule,
  ColDef,
  GridOptions,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  ModuleRegistry,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { DeviceService } from '../services/device-service/device.service';
import {
  AG_GRID_PAGE_SIZE_OPTIONS, AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  resolveAgGridLayoutRowCount,
  syncInfiniteRowModelPageSize,
} from '../shared/ag-grid/ag-grid-pagination.util';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../shared/ag-grid/ag-grid-date-column.util';
import { get } from 'lodash'; // for copy data cell value

ModuleRegistry.registerModules([AllCommunityModule]);
import { AgGridCopyCellTooltipDirective } from '../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
import { AgGridCopyCellTooltipOptions } from '../shared/ag-grid/ag-grid-copy-cell-tooltip.util';

/** Wait after last keystroke before applying text filters / calling search. */
const FILTER_DEBOUNCE_MS = 400;

/** Simple text floating filter — no advanced/funnel menu (same as device list). */
const TEXT_FILTER_PARAMS = {
  filterOptions: ['contains'],
  defaultOption: 'contains' as const,
  trimInput: true,
  maxNumConditions: 1,
  suppressFilterButton: true,
  debounceMs: FILTER_DEBOUNCE_MS,
};

@Component({
  selector: 'app-device-health',
  standalone: true,
  imports: [CommonModule, AgGridAngular, AgGridModule, AgGridCopyCellTooltipDirective],
  templateUrl: './device-health.component.html',
  styleUrl: './device-health.component.scss'
})
export class DeviceHealthComponent implements OnInit, OnChanges, OnDestroy {
  @Input() gatewayId!: string;
  rowData: any[] = [];
  columnDefs: ColDef[] = [];
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  gridApi: any;
  totalElements = 0;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  /** Hover shows full cell text when values are truncated. */
  readonly copyCellTooltipConfig: AgGridCopyCellTooltipOptions = {
    preferCellText: true,
  };

  private getRowsFilterTimer: ReturnType<typeof setTimeout> | null = null;
  private searchSub: Subscription | null = null;
  private lastFilterModelKey: string | null = null;

  get gridLayoutRowCount(): number {
    return resolveAgGridLayoutRowCount(this.paginationPageSize, this.totalElements);
  }

  private readonly dateColumnFilterParams = createUtcDateFilterParams();

  searchPayload = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    sorts: [{ field: 'treeId', direction: 'asc' }],
    filters: [] as { field: string; operator: string; value: unknown }[],
  };

  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    floatingFilter: true,
    filter: true,
    resizable: true,
    // No flex:1 — fixed/min widths drive layout; horizontal scroll when columns exceed viewport.
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    tooltipValueGetter: (params) => {
      if (params?.valueFormatted != null && params.valueFormatted !== '') {
        return String(params.valueFormatted);
      }
      const v = params?.value;
      if (v === undefined || v === null || v === '') {
        return '';
      }
      if (typeof v === 'object') {
        try {
          return JSON.stringify(v);
        } catch {
          return '';
        }
      }
      return String(v);
    },
  };
  gridOptions: GridOptions = {
    domLayout: 'normal',
    suppressHorizontalScroll: false,
    alwaysShowHorizontalScroll: false,
    enableBrowserTooltips: true,
    tooltipShowDelay: 400,
  };
  constructor(private deviceService: DeviceService) {}

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.searchPayload.size = newPageSize;
        this.searchPayload.page = 0;
      }
    );
  }

  ngOnInit(): void {
    this.setupColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gatewayId'] && this.gridApi) {
      this.lastFilterModelKey = null;
      this.refreshGrid();
    }
  }

  ngOnDestroy(): void {
    this.clearFilterTimers();
    this.searchSub?.unsubscribe();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  private clearFilterTimers(): void {
    if (this.getRowsFilterTimer) {
      clearTimeout(this.getRowsFilterTimer);
      this.getRowsFilterTimer = null;
    }
  }

  private refreshGrid(): void {
    if (!this.gridApi) {
      return;
    }
    this.lastFilterModelKey = null;
    this.searchPayload.page = 0;
    this.gridApi.paginationGoToFirstPage();
    this.gridApi.purgeInfiniteCache();
  }

  /**
   * Size host to the loaded page of rows (default fixed row height).
   * Device Health is not a comment/auto-height grid — use count × default row px.
   * Full content height so the grid does not need an internal vertical scrollbar.
   * Extra px reserves space for the AG Grid horizontal scrollbar when columns overflow.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const horizontalScrollBarPx = 17;
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx + horizontalScrollBarPx;
    const minHostPx =
      AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX + horizontalScrollBarPx;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
  }

  dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      if (!this.gatewayId) {
        this.rowData = [];
        this.totalElements = 0;
        this.syncGridHostHeight(0);
        params.successCallback([], 0);
        return;
      }

      const filterKey = JSON.stringify(params.filterModel ?? {});
      // First load / sort / page: search immediately.
      // Filter typing: wait until keystrokes pause.
      const filtersChanged =
        this.lastFilterModelKey !== null && filterKey !== this.lastFilterModelKey;

      const runSearch = () => {
        this.lastFilterModelKey = filterKey;
        this.executeSearch(params);
      };

      if (filtersChanged) {
        if (this.getRowsFilterTimer) {
          clearTimeout(this.getRowsFilterTimer);
        }
        this.getRowsFilterTimer = setTimeout(() => {
          this.getRowsFilterTimer = null;
          runSearch();
        }, FILTER_DEBOUNCE_MS);
        return;
      }

      runSearch();
    }
  };

  private executeSearch(params: IGetRowsParams): void {
    this.sortForAgGrid(params.sortModel);
    this.filterForAgGrid(params.filterModel);
    this.searchPayload.page = Math.floor(params.startRow / this.paginationPageSize);
    this.searchPayload.size = this.paginationPageSize;

    this.searchSub?.unsubscribe();
    this.searchSub = this.deviceService.searchDeviceHealth(this.gatewayId, this.searchPayload).subscribe({
      next: (page: any) => {
        this.rowData = page?.content ?? [];
        this.totalElements = page?.totalElements ?? this.rowData.length;
        this.syncGridHostHeight(this.rowData.length);
        params.successCallback(this.rowData, this.totalElements);
        this.autoSizeContentColumns();
      },
      error: (err) => {
        console.error('Device Health search error:', err);
        this.rowData = [];
        this.totalElements = 0;
        this.syncGridHostHeight(0);
        params.failCallback();
      }
    });
  }

  /** Widen columns to fit rendered cell/header text (horizontal scroll if needed). */
  private autoSizeContentColumns(): void {
    if (!this.gridApi) {
      return;
    }
    setTimeout(() => {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.autoSizeColumns(
        [
          'peripheralName',
          'healthValue',
          'did',
          'deviceType',
          'manufacturerCode',
          'manufacturer',
          'otherProperties',
        ],
        false
      );
    });
  }

  private filterForAgGrid(filterModel: Record<string, any> | null | undefined): void {
    const newFilters: { field: string; operator: string; value: unknown }[] = [];
    if (filterModel) {
      for (const field of Object.keys(filterModel)) {
        const mapped = mapAgGridFilterToApi(field, filterModel[field]);
        if (mapped && mapped.field && mapped.operator) {
          newFilters.push(mapped);
        }
      }
    }
    this.searchPayload.filters = newFilters;
  }

  private sortForAgGrid(sortModel: { colId: string; sort: string }[] | null | undefined): void {
    if (!sortModel?.[0]?.colId) {
      this.searchPayload.sorts = [{ field: 'treeId', direction: 'asc' }];
      return;
    }
    this.searchPayload.sorts = [{
      field: sortModel[0].colId,
      direction: sortModel[0].sort === 'desc' ? 'desc' : 'asc',
    }];
  }

  /* Start Copy Data cell Value function   */
  contextMenu: { visible: boolean; x: number; y: number; value: any } = {
    visible: false,
    x: 0,
    y: 0,
    value: ''
  };
  selectedCellValue: string = '';
  contextMenuVisible: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  getNestedProperty(obj: any, path: string): any {
    return get(obj, path, 'N/A');
  }

  onCellContextMenu(event: any) {
    event.event.preventDefault();

    let cellValue;
    const columnField = event.colDef.field;

    if (event.colDef.valueGetter) {
      cellValue = event.colDef.valueGetter({ data: event.data });
    } else {
      cellValue = this.getNestedProperty(event.data, columnField);
    }

    this.contextMenu = {
      visible: true,
      x: event.event.clientX,
      y: event.event.clientY,
      value: cellValue || 'N/A',
    };
  }

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value).then(() => {
      this.contextMenu.visible = false;
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.contextMenu.visible = false;
  }
  preventDefaultContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }
  /* End Copy Data cell Value function   */

  safeValue = (field: string) => {
    return (params: any) => {
      const value = params.data?.[field];
      if (value === undefined || value === null || value === '') {
        return 'N/A';
      }
      return value;
    };
  };

  numericDisplay = (field: string) => {
    return (params: any) => {
      const v = params.data?.[field];
      return (v === undefined || v === null || v === '') ? 'N/A' : v;
    };
  };

  formatJsonValue = (field: string) => {
    return (params: any): string => {
      const value = params.data?.[field];
      if (value === undefined || value === null || value === '') {
        return 'N/A';
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return 'N/A';
        }
      }
      if (typeof value === 'string') {
        return value;
      }
      return String(value);
    };
  };

  setupColumns(): void {
    this.columnDefs = [
      {
        headerName: 'TreeID',
        field: 'treeId',
        width: 100,
        minWidth: 90,
        valueGetter: this.safeValue('treeId'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Peripheral Name',
        field: 'peripheralName',
        width: 280,
        minWidth: 240,
        valueGetter: this.safeValue('peripheralName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      withDateColumnLayout({
        headerName: 'Last Seen (UTC)',
        field: 'lastSeen',
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        filterParams: this.dateColumnFilterParams,
        cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.lastSeen),
      }),
      withDateColumnLayout({
        headerName: 'Last DTree Timestamp (UTC)',
        field: 'gatewayTimestamp',
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        filterParams: this.dateColumnFilterParams,
        cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.gatewayTimestamp),
      }),
      {
        headerName: 'Time Lag(s)',
        field: 'lagFromGatewayInSeconds',
        width: 120,
        minWidth: 110,
        valueFormatter: this.numericDisplay('lagFromGatewayInSeconds'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Health Value',
        field: 'healthValue',
        width: 140,
        minWidth: 120,
        valueFormatter: this.numericDisplay('healthValue'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Device ID',
        field: 'did',
        width: 340,
        minWidth: 300,
        valueGetter: this.safeValue('did'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Device Type',
        field: 'deviceType',
        width: 160,
        minWidth: 140,
        valueGetter: this.safeValue('deviceType'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Manufacturer Code',
        field: 'manufacturerCode',
        width: 180,
        minWidth: 160,
        valueGetter: this.safeValue('manufacturerCode'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Manufacturer',
        field: 'manufacturer',
        width: 180,
        minWidth: 160,
        valueGetter: this.safeValue('manufacturer'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      },
      {
        headerName: 'Other',
        field: 'otherProperties',
        width: 280,
        minWidth: 220,
        valueGetter: this.formatJsonValue('otherProperties'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        suppressFloatingFilterButton: true,
      }
    ];
  }
}
